import { CSSProperties, useState } from "react";
import { clsx, formatNumberToDisplayType, getCampaignTypeInfo, getFallbackCampaignName, isUsingConversionsApi, makeKey, pluralize, styx } from "modules/Utils";
import Sty from "modules/Sty";
import TagSymphony from "./TagSymphony";
import Stat, { StatItem } from "./Stat";
import ButtonSymphony from "./ButtonSymphony";
import CampaignStatusTag from "./CampaignStatusTag";
import ViewButton from "./ViewButton";
import { CampaignDetails, EmailCampaignData, PlaylistDatav2, CurrentBrand } from "modules/Types";
import { OrganizationCampaignData } from "api/organizationApi";
import { CampaignStatus, CampaignType, DEFAULT_CURRENCY, PlaybookType } from "modules/Const";
import useScreen from "Hooks/useScreen";
import dayjs from "dayjs";
import AdminShowCampaignInfo from "pages/MarketingPage/Components/Modals/AdminShowCampaignInfo";
import useUser from "Hooks/useUser";
import DebugBox from "components/shareable/DebugBox";
import { getCampaignTitleByCampaignType, getGeographiesTitleByGeographyType } from "pages/MarketingPage/VideoAds/constants";
import millify from "millify";
import CampaignImageSymphony from "./CampaignImageSymphony";
import BrandInfo from "./BrandInfo";
import { useHistory } from "react-router-dom";
import Icon from "./Icon";
import { FacebookCampaignSubmissionType } from "modules/Campaign";
import useAppView from "Hooks/useAppView";

interface CampaignInfoSymphonyProps {
    className: string;
    style?: CSSProperties;
    campaign: CampaignDetails | EmailCampaignData
    isBorderBottom?: boolean;
    stats?: StatItem[];
    onClick?: (campaign: CampaignDetails | EmailCampaignData) => void;
    onClickExternal?: () => void;
    childrenButtons?: React.ReactNode;
    brandInfo?: CurrentBrand | OrganizationCampaignData['brandInfo'];
}

// NOTE: component()
const CampaignInfoSymphony = ({
    className,
    style,
    campaign: campaignProp,
    isBorderBottom = true,
    stats: statsProp = [],
    onClick,
    onClickExternal,
    childrenButtons,
    brandInfo,
}: CampaignInfoSymphonyProps) => {
    const history = useHistory();
    const { mediumView, mStyle } = useScreen();
    const { isSymphonyAdmin } = useUser();
    const { isTeamView } = useAppView();

    let stats = statsProp

    const {
        id: campaignId,
        campaign_metadata: campaignMetadataGeneric,
        createdAt,
        status
    } = campaignProp

    const {
        campaign_type: campaignType,
    } = campaignMetadataGeneric

    const isEmail = campaignType === CampaignType.EMAIL_FANBASE

    // NOTE: CAMPAIGN DETAILS
    const campaign = campaignProp as CampaignDetails
    const campaignMetadata = campaign.campaign_metadata
    const currencyCode = (campaignMetadata as any)?.currency?.code || (campaignMetadata as any)?.currency || DEFAULT_CURRENCY

    // NOTE: EMAIL CAMPAIGN DETAILS
    const emailCampaign = campaignProp as EmailCampaignData
    const emailCampaignMetadata = emailCampaign.campaign_metadata

    const isFbCampaign = campaignType === CampaignType.BOOST_INSTAGRAM ||
        campaignType === CampaignType.LINK_CLICKS ||
        campaignType === CampaignType.RECORD_STREAMS ||
        campaignType === PlaybookType.GROW_PLAYLIST_FOLLOWERS;

    const [isAdminShowCampaignInfoModal, setIsAdminShowCampaignInfoModal] = useState(false);

    if (isEmail) {
        stats = [
            {
                title: 'Emails Sent',
                value: emailCampaignMetadata.email_fanbase.sentTo,
                displayType: 'number',
            },
            // {
            //     title: 'Emails Opened',
            //     value: emailCampaignMetadata.email_fanbase.openedBy,
            //     displayType: 'number',
            // },
        ]
    }

    const renderInfoTags = () => {

        let campaignTypeDefault = campaignMetadata.playbook || campaignType
        let usesExpressSetup = (campaignMetadata.v2_campaign_input as FacebookCampaignSubmissionType)?.usesExpressSetup || false
        return (
            <div
                className="campaignCardSymphonyInfoTags"
                style={sty.campaignCardSymphonyInfoTags}
            >
                <TagSymphony
                    icon={getCampaignTypeInfo(campaignTypeDefault).icon}
                    title={getCampaignTypeInfo(campaignTypeDefault).title}
                    className="campaignCardSymphonyInfoTag"
                    type="square"
                />
                {!usesExpressSetup && isUsingConversionsApi(campaignMetadata) &&
                    <TagSymphony
                        className="campaignCardSymphonyInfoTag"
                        icon="boost"
                        title="Using Symphony Conversions"
                        type="square"
                    />
                }
                {usesExpressSetup &&
                    <TagSymphony
                        className="campaignCardSymphonyInfoTag2"
                        icon="boost"
                        title="Express Setup"
                        type="square"
                    />
                }
            </div>
        )
    }

    const renderStatus = () => {
        return (
            <CampaignStatusTag
                status={status}
                className="campaignCardSymphonyStatus"
            />
        )
    }

    const renderTitle = () => {
        const defaultCampaignTitle = getFallbackCampaignName(campaignMetadata)

        return (
            <div className="campaignCardSymphonyTitleContainer" style={sty.campaignCardSymphonyTitleContainer}>
                <div
                    className="campaignCardSymphonyTitle"
                    style={styx(
                        mediumView ? Sty.text18 : Sty.text20,
                        {
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            width: '100%',
                        }
                    )}
                >
                    {defaultCampaignTitle}
                </div>
                {isTeamView() && brandInfo && (
                    <BrandInfo
                        className="campaignCardSymphonyBrandInfo"
                        brandName={brandInfo.name}
                        brandImage={brandInfo.image || undefined}
                        brandSlug={brandInfo.slug}
                        variant="small"
                        titleColor={Sty.text12grey.color}
                    />
                )}
            </div>
        )
    }

    const renderDescriptionFirstLine = () => {
        if (isEmail) {
            const emailReleaseDate = dayjs(emailCampaignMetadata.release_date).format('MMM D, YYYY')
            return `Release Date: ${emailReleaseDate}`
        }

        // Get date information, checking v2_campaign_input if it exists
        let startDateStr = campaignMetadata.startDate
        let endDateStr = campaignMetadata.endDate

        // Only attempt to access v2_campaign_input if it exists
        if (campaignMetadata.v2_campaign_input?.lengthConfig) {
            // Use type assertion to avoid TypeScript errors
            const lengthConfig = campaignMetadata.v2_campaign_input.lengthConfig as any
            if (lengthConfig.finalStartDate) {
                startDateStr = lengthConfig.finalStartDate
            } else if (lengthConfig.startDate) {
                startDateStr = lengthConfig.startDate
            }

            if (lengthConfig.finalEndDate) {
                endDateStr = lengthConfig.finalEndDate
            } else if (lengthConfig.endDate) {
                endDateStr = lengthConfig.endDate
            }
        }

        const startDate = dayjs(startDateStr).format('MMM D, YYYY')
        const endDate = dayjs(endDateStr).format('MMM D, YYYY')
        const daysLeft = dayjs(endDateStr).diff(dayjs(), 'days') + 1
        const totalDays = dayjs(endDateStr).diff(dayjs(startDateStr), 'days') + 1

        let budgetValue = campaignMetadata.v2_campaign_input?.budget || campaignMetadata.budget
        switch (campaignType) {
            case CampaignType.PRE_SAVE:
                return `${campaign?.presavesData?.total} Pre-Saves & ${campaign?.emailsData?.new} New Email`
            case PlaybookType.INCREASE_VIDEO_VIEWS:
                const {
                    targetingType,
                    geographies,
                } = campaignMetadata as any || {}
                const budgetVideoLocal = formatNumberToDisplayType(Number(budgetValue), 'currency', currencyCode)
                return `Budget: ${budgetVideoLocal} | Goal: ${getCampaignTitleByCampaignType(targetingType)} | Targeting: ${getGeographiesTitleByGeographyType(geographies.type)}`
            case PlaybookType.GET_PLAYLISTED:
                const {
                    price_data: {
                        price: pricePlaylisting = 0,
                        tier: playlistingTier = 0,
                        playlists: playlistsList
                    },
                } = campaignMetadata as any || {}
                const pricePlaylistingLocal = pricePlaylisting?.toLocaleString('en-US', { style: 'currency', currency: currencyCode })

                let getPlaylistedSubtitle = `Goal: ${typeof playlistingTier === 'number' ? millify(playlistingTier) : playlistingTier} Followers`

                if (playlistsList && playlistsList.length > 0) {
                    const pitchedPlaylists = playlistsList?.filter((playlist: PlaylistDatav2) => (playlist.playlistStatus && playlist.playlistStatus === 'Pitched'))
                    const acceptedPlaylists = playlistsList?.filter((playlist: PlaylistDatav2) => (playlist.playlistStatus && playlist.playlistStatus === 'Accepted'))

                    if (acceptedPlaylists.length > 0) {
                        getPlaylistedSubtitle += ` | Added to ${acceptedPlaylists.length} Playlists`
                    }

                    if (pitchedPlaylists.length > 0) {
                        getPlaylistedSubtitle += ` | Pitching to: ${pitchedPlaylists.length} Playlists`
                    }
                }
                return `Budget: ${pricePlaylistingLocal} | ${getPlaylistedSubtitle}`
        }

        const formattedBudget = formatNumberToDisplayType(Number(budgetValue), 'currency', currencyCode)
        
        if (daysLeft <= 0) {
            return `Budget: ${formattedBudget} | Ran from ${startDate} to ${endDate} (${totalDays} ${pluralize('Day', totalDays)})`
        }

        let startingPointText = `Budget: ${formattedBudget} | Running from ${startDate} to ${endDate}`
        if (daysLeft <= 0) {
            startingPointText += ` | Days Left: ${daysLeft}`
        }
        return startingPointText
    }

    const renderDescription = () => {
        const endDate = dayjs(campaignMetadata.endDate).format('MMM D, YYYY')
        let descriptionSecondLine = ''
        if (campaignType === CampaignType.PRE_SAVE) {
            if (status === CampaignStatus.ACTIVE) {
                const daysLeft = dayjs(campaignMetadata.endDate).diff(dayjs(), 'days') + 1
                let daysLeftText = daysLeft === 1 ? 'day' : 'days'
                descriptionSecondLine = `Releases in ${daysLeft} ${daysLeftText} (${endDate})`
            } else {
                descriptionSecondLine = `Released on ${endDate}`
            }
        }

        return (
            <div
                className="campaignCardSymphonyDescription"
                style={{
                    ...Sty.text14grey,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                }}
            >
                <div className="campaignCardSymphonyDescriptionFirstLine">
                    {/* <DebugBox
                        alwaysShow
                        data={{
                            campaign
                        }}
                    /> */}
                    {renderDescriptionFirstLine()}
                </div>
                {descriptionSecondLine &&
                    <div className="campaignDescriptionSecondLine">
                        {descriptionSecondLine}
                    </div>
                }
                {/* {campaignType === CampaignType.pre_save ?
                    'Released on: '
                    :
                    'Created on: '} {campaignType === CampaignType.pre_save ? endDate : dayjs(createdAt).format('MMM D, YYYY')
                } */}
                <div className="campaignIdInfo" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    Campaign ID: {campaignId} • Created on: {dayjs(createdAt).format('MMM D, YYYY')}
                    {onClickExternal &&
                        <Icon
                            className="campaignCardSymphonyButtonIcon"
                            style={{
                                marginRight: 8,
                            }}
                            icon="externalLink"
                            onClick={onClickExternal}
                        />
                    }
                </div>
            </div>
        )
    }

    const renderStats = () => {
        return (
            <div
                className="campaignCardSymphonyStats"
                style={sty.campaignCardSymphonyStats}
            >
                {stats.map((stat, index) => (
                    <Stat
                        key={makeKey(stat.title, index)}
                        className="campaignCardSymphonyStat"
                        stats={[stat]}
                        isReverse
                        isBorder={false}
                        style={{ width: 'fit-content' }}
                    />
                ))}
            </div>
        )
    }

    const renderButtons = () => {
        if (!onClick) {
            return null
        }

        return (
            <div
                className="campaignCardSymphonyButtonContainer"
                style={mStyle(sty, 'campaignCardSymphonyButtonContainer')}
            >
                {isSymphonyAdmin && isFbCampaign && !mediumView &&
                    <ButtonSymphony
                        className="adminCampaignInfoButton"
                        onClick={() => setIsAdminShowCampaignInfoModal(true)}
                        variant="outlined"
                        size="small"
                    >
                        Admin: Campaign info
                    </ButtonSymphony>
                }
                <div
                    className="campaignCardSymphonyButtonContainer"
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 8,
                        width: '100%',
                    }}
                >
                    <ViewButton
                        className="campaignCardSymphonyButton"
                        style={{
                            width: '100%',
                        }}
                        onClick={() => onClick(campaignProp)}
                        fullWidth={true}
                    />
                </div>
            </div>
        )
    }

    const renderCampaignImage = () => {
        return (
            <CampaignImageSymphony
                className="campaignCardSymphonyCampaignImage"
                campaignMetadata={campaignMetadata}
            />
        )
    }

    return (
        <div
            className={clsx(
                "campaignCardSymphonyElement",
                className,
            )}
            style={styx(
                mStyle(sty, 'campaignCardSymphonyElement'),
                isBorderBottom && { borderBottom: Sty.border },
                style,
            )}
        >
            {!mediumView && renderCampaignImage()}

            <div
                className="campaignCardSymphonyInner"
                style={mStyle(sty, 'campaignCardSymphonyInner')}
            >
                <div
                    className="infoTypeStatusContainer"
                    style={sty.infoTypeStatusContainer}
                >
                    {mediumView && renderCampaignImage()}
                    <div
                        className="infoInnerDetails"
                        style={mStyle(sty, 'infoInnerDetails')}
                    >
                        {mediumView && renderTitle()}
                        {!mediumView && renderInfoTags()}
                        {renderStatus()}
                    </div>
                    {mediumView && renderStats()}
                </div>
                <div
                    className="infoTitleAndDescriptionContainer"
                    style={sty.infoTitleAndDescriptionContainer}
                >
                    {mediumView && renderInfoTags()}
                    {!mediumView && renderTitle()}
                    {renderDescription()}
                </div>
            </div>

            {!mediumView && renderStats()}

            {childrenButtons || renderButtons()}

            {campaignId && isFbCampaign && isAdminShowCampaignInfoModal &&
                <AdminShowCampaignInfo
                    isOpen={true}
                    onClose={() => setIsAdminShowCampaignInfoModal(false)}
                    campaignId={campaignId}
                />
            }
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    campaignCardSymphonyElement: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
        paddingTop: 16,
        paddingBottom: 16,
        paddingLeft: 16,
        paddingRight: 16,
    },
    campaignCardSymphonyElement_medium: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        paddingTop: 16,
        paddingBottom: 16,
        paddingLeft: 0,
        paddingRight: 0,
    },
    campaignCardSymphonyIconContainer: {
        flexShrink: 0,
    },
    campaignCardSymphonyInner: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        maxWidth: 500,
    },
    campaignCardSymphonyInner_medium: {
        maxWidth: '100%',
        gap: 16,
    },
    infoTypeStatusContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    campaignCardSymphonyStats: {
        flex: 4,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 32,
    },
    campaignCardSymphonyButtonContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
        width: 'fit-content',
        gap: 8,
    },
    campaignCardSymphonyButtonContainer_medium: {
        flexDirection: 'column',
        width: '100%',
    },
    infoTitleAndDescriptionContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    infoInnerDetails: {
        display: 'flex',
        flexDirection: 'row',
        gap: 4,
        overflow: 'hidden',
    },
    infoInnerDetails_medium: {
        flexDirection: 'column',
    },
    campaignCardSymphonyInfoTags: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        flexShrink: 0,
        flexWrap: 'wrap',
    },
    campaignCardSymphonyTitleContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
}

export default CampaignInfoSymphony;